import { useEffect, useState } from 'react';

import { projects as projectsApi } from '@/helpers/ApiHelper';
import type { Project } from '@/types/project';

interface UseProjectsResult {
  projects: Project[];
  isLoading: boolean;
  error: Error | null;
}

/**
 * The shared project registry, fetched once per mount.
 *
 * The registry is one row per registered repo — small enough that every
 * surface refetching it is cheaper than adding a cache layer.
 */
export function useProjects(): UseProjectsResult {
  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let cancelled = false;

    projectsApi
      .list()
      .then((result) => {
        if (!cancelled) {
          setProjects(result.results);
          setError(null);
        }
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setError(e instanceof Error ? e : new Error(String(e)));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return { projects, isLoading, error };
}
